import { memo, useMemo, useState } from 'react';
import { formatCurrency } from '../../utilities/general-utils';

const SnowballProgress = memo(({ 
    aggregateResults, 
    accounts, 
    showDetailedBreakdown = false,
    showPaymentSchedules = true
}) => {
    const progressData = useMemo(() => {
        if (!aggregateResults) {
            return null;
        }

        const {
            totalPaid,
            totalInterestPaid,
            totalPrincipal,
            totalMonths,
            averageMonthlyPayment
        } = aggregateResults;

        const interestPercentage = totalPrincipal > 0 ? (totalInterestPaid / totalPrincipal) * 100 : 0;
        const monthlyInterest = totalMonths > 0 ? totalInterestPaid / totalMonths : 0;
        const payoffYears = totalMonths / 12;

        return {
            totalPaid,
            totalInterestPaid,
            totalPrincipal,
            totalMonths,
            averageMonthlyPayment,
            interestPercentage,
            monthlyInterest,
            payoffYears,
            accountCount: aggregateResults.accountsBreakdown ? aggregateResults.accountsBreakdown.length : (accounts ? accounts.length : 0)
        };
    }, [aggregateResults, accounts]);

    const accountBreakdown = useMemo(() => {
        // Use pre-calculated breakdown from aggregateResults if available
        if (aggregateResults && aggregateResults.accountsBreakdown) {
            return aggregateResults.accountsBreakdown;
        }

        // Fallback to calculating from accounts (for backward compatibility)
        if (!accounts) {
            return [];
        }

        return accounts.map((account, index) => ({
            id: account.id,
            name: account.name,
            balance: account.balance,
            months: account.months || 0,
            totalPayments: account.totalPayments || account.balance,
            totalInterest: account.totalInterest || 0,
            interestRate: account.interestRate || 0,
            order: index + 1
        }));
    }, [aggregateResults, accounts]);

    const paymentSchedules = useMemo(() => {
        // Use pre-calculated schedules from aggregateResults if available
        if (aggregateResults && aggregateResults.paymentSchedules) {
            // convert object to array for sorting
            const schedulesArray = Object.entries(aggregateResults.paymentSchedules)
                .map(([accountId, schedule]) => ({
                    accountId,
                    accountName: schedule.accountName,
                    payments: schedule.payments
                }))
                .sort((a, b) => a.payments.length - b.payments.length);
            return schedulesArray;
        }
        return [];
    }, [aggregateResults]);

    // State for payment number filter
    const [selectedPaymentNumber, setSelectedPaymentNumber] = useState(null);

    // Get the maximum payment number across all schedules
    const maxPaymentNumber = useMemo(() => {
        if (paymentSchedules.length === 0) return 0;
        return Math.max(...paymentSchedules.map(schedule => 
            Math.max(...schedule.payments.map(payment => payment.paymentNumber))
        ));
    }, [paymentSchedules]);

    if (!progressData) {
        return (
            <div style={{ 
                padding: '2rem', 
                textAlign: 'center', 
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
            }}>
                <p>Calculate payment schedules to see your snowball progress</p>
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '2rem', 
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginBottom: '2rem'
        }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>
                Your Debt Snowball Progress
            </h3>

            {/* Key Metrics */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '4px' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5234' }}>
                        {formatCurrency(progressData.totalPrincipal)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Debt</div>
                </div>

                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e65100' }}>
                        {progressData.payoffYears.toFixed(1)} years
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Time to Freedom</div>
                </div>

                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#fce4ec', borderRadius: '4px' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c2185b' }}>
                        {formatCurrency(progressData.totalInterestPaid)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Interest</div>
                </div>

                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
                        {formatCurrency(progressData.averageMonthlyPayment)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Avg Monthly Payment</div>
                </div>
            </div>

            {/* Summary Stats */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginBottom: showDetailedBreakdown ? '2rem' : '0'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>Interest Rate</div>
                    <div>{progressData.interestPercentage.toFixed(2)}% of principal</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>Accounts</div>
                    <div>{progressData.accountCount} debt accounts</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>Total Paid</div>
                    <div>{formatCurrency(progressData.totalPaid)}</div>
                </div>
            </div>

            {/* Detailed Breakdown - Always show if we have account data */}
            {accountBreakdown.length > 0 && (
                <div>
                    <h4 style={{ margin: '0 0 1rem 0' }}>Payoff Order</h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #ddd' }}>Order</th>
                                    <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #ddd' }}>Account</th>
                                    <th style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>Balance</th>
                                    <th style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>APR</th>
                                    <th style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>Months</th>
                                    <th style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>Total Interest</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accountBreakdown.map((account, index) => (
                                    <tr key={account.id}>
                                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>#{index + 1}</td>
                                        <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{account.name}</td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                            {formatCurrency(account.balance)}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                            {(account.interestRate * 100).toFixed(2)}%
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                            {account.months}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                            {formatCurrency(account.totalInterest)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Payment Schedules - Individual tables per account */}
            {showPaymentSchedules && paymentSchedules.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0' }}>Payment Schedules</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label htmlFor="paymentFilter" style={{ fontSize: '0.9rem', color: '#666' }}>
                                Filter by Payment #:
                            </label>
                            <select
                                id="paymentFilter"
                                value={selectedPaymentNumber || ''}
                                onChange={(e) => setSelectedPaymentNumber(e.target.value ? parseInt(e.target.value) : null)}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <option value="">Show all payments</option>
                                {Array.from({ length: maxPaymentNumber }, (_, i) => i + 1).map(num => (
                                    <option key={num} value={num}>Payment #{num}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {paymentSchedules.map((schedule) => (
                        <div key={schedule.accountId} style={{ marginBottom: '2rem' }}>
                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
                                {schedule.accountName}
                            </h5>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #ddd' }}>Payment #</th>
                                            <th style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #ddd' }}>Date</th>
                                            <th style={{ padding: '0.4rem', textAlign: 'right', border: '1px solid #ddd' }}>Payment</th>
                                            <th style={{ padding: '0.4rem', textAlign: 'right', border: '1px solid #ddd' }}>Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schedule.payments
                                            .filter(payment => selectedPaymentNumber === null || payment.paymentNumber === selectedPaymentNumber)
                                            .map((payment, index) => (
                                                <tr key={index}>
                                                    <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #ddd' }}>
                                                        {payment.paymentNumber}
                                                    </td>
                                                    <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #ddd' }}>
                                                        {/* yyyy-MM-dd */}
                                                        {new Date(payment.date).toISOString().split('T')[0]}
                                                    </td>
                                                    <td style={{ padding: '0.4rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                                        {formatCurrency(payment.payment)}
                                                    </td>
                                                    <td style={{ padding: '0.4rem', textAlign: 'right', border: '1px solid #ddd' }}>
                                                        {formatCurrency(payment.balance)}
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

SnowballProgress.displayName = 'SnowballProgress';

export default SnowballProgress;